import { useMemo, type CSSProperties } from "react";
import type { ScenarioSensitivity } from "../../../services/api";
import { formatAmount, formatLever } from "../scenarioFormat";
import { shade } from "../scenarioLayout";

/**
 * Two levers against each other, so an executive sees how robust the answer
 * is rather than one point estimate. The scenario being viewed is outlined.
 */
export function SensitivityGrid({ grid }: { grid: ScenarioSensitivity }) {
  const [minimum, maximum] = useMemo(() => {
    const values = grid.values
      .flat()
      .filter((item): item is number => item != null && Number.isFinite(item));
    return values.length ? [Math.min(...values), Math.max(...values)] : [0, 0];
  }, [grid]);
  const rowSigned = grid.rows.some((item) => item < 0);
  const columnSigned = grid.columns.some((item) => item < 0);

  return (
    <div className="panel-table-wrap">
      <table className="panel-table sensitivity-grid">
        <caption className="sr-only">{grid.title}</caption>
        <thead>
          <tr>
            <th scope="col">
              {grid.rowLabel} ↓ / {grid.columnLabel} →
            </th>
            {grid.columns.map((column) => (
              <th
                scope="col"
                key={column}
                className={`numeric${column === grid.currentColumn ? " current" : ""}`}
              >
                {formatLever(column, grid.columnUnit, columnSigned)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.rows.map((row, rowIndex) => (
            <tr key={row}>
              <th scope="row" className={row === grid.currentRow ? "current" : ""}>
                {formatLever(row, grid.rowUnit, rowSigned)}
              </th>
              {grid.columns.map((column, columnIndex) => {
                const value = grid.values[rowIndex]?.[columnIndex] ?? null;
                const level = shade(value, minimum, maximum);
                const negative = value != null && value < 0;
                const current =
                  row === grid.currentRow && column === grid.currentColumn;
                return (
                  <td
                    key={column}
                    className={`numeric sensitivity-cell${current ? " current" : ""}${negative ? " negative" : ""}`}
                    style={
                      level == null
                        ? undefined
                        : ({ "--level": level.toFixed(3) } as CSSProperties)
                    }
                    aria-current={current ? "true" : undefined}
                  >
                    {formatAmount(value, grid.valueUnit)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
